/**
 * 다국어 지원 유틸리티
 * 언어별 텍스트 번역을 관리합니다.
 */

export type Language = 'ko' | 'en'

export const translations = {
  ko: {
    // 공통
    common: {
      loading: '로딩 중...',
      error: '에러',
      cancel: '취소',
      confirm: '확인',
      later: '나중에',
      upgrade: '업그레이드',
      save: '저장',
      delete: '삭제',
      edit: '수정',
      create: '생성',
      close: '닫기',
      back: '뒤로가기',
      share: '공유',
      shared: '공유중',
      completed: '완료',
      inProgress: '진행중',
      preparing: '준비중',
      yes: '예',
      no: '아니오',
      search: '검색',
      filter: '필터',
      all: '전체',
      none: '없음',
      today: '오늘',
      yesterday: '어제',
      tomorrow: '내일',
      info: '정보',
      refresh: '새로고침',
      backToLogin: '로그인으로 돌아가기',
      or: '또는',
      change: '변경',
      go: '이동',
      view: '보기',
      group: '그룹',
      account: '계좌',
    },
    // 통화 단위
    currency: {
      unit: '원',
    },
    // 페이지 제목
    pages: {
      dashboard: '통계',
      dreams: '꿈',
      goals: '목표',
      plans: '계획',
      checklist: '체크리스트',
      calendar: '캘린더',
      coaching: '코칭',
      share: '피드',
      userManagement: '유저 관리',
      settings: '설정',
      guide: '사용 설명서',
      admin: '관리자',
    },
    // 메시지
    messages: {
      noData: '데이터가 없습니다.',
      createSuccess: '생성되었습니다.',
      updateSuccess: '수정되었습니다.',
      deleteSuccess: '삭제되었습니다.',
      deleteConfirm: '정말로 삭제하시겠습니까?',
      deleteConfirmTitle: '삭제 확인',
      accountCreated: '계좌가 자동으로 생성되었습니다.',
      dreamCopied: '공유된 꿈을 내 꿈으로 가져왔습니다.',
      createFailed: '생성에 실패했습니다.',
      updateFailed: '수정에 실패했습니다.',
      deleteFailed: '삭제에 실패했습니다.',
      shareFailed: '공유 설정에 실패했습니다.',
      copyFailed: '가져오기에 실패했습니다.',
      likeFailed: '좋아요 처리에 실패했습니다.',
      coachDeleteFailed: '코치 삭제에 실패했습니다.',
      coachGenerateSuccess: '코칭이 생성되었습니다.',
      coachGenerateFailed: '코치 생성에 실패했습니다.',
      generating: '생성중입니다...',
      actionCreateFailed: '할일 생성에 실패했습니다.',
      actionUpdateFailed: '할일 수정에 실패했습니다.',
      actionDeleteFailed: '할일 삭제에 실패했습니다.',
      shareSuccess: '공유되었습니다.',
      unshareSuccess: '공유가 해제되었습니다.',
              itemCreateFailed: '아이템 생성에 실패했습니다.',
              itemUpdateFailed: '아이템 수정에 실패했습니다.',
              itemDeleteFailed: '아이템 삭제에 실패했습니다.',
              statusChangeFailed: '상태 변경에 실패했습니다.',
      planSelectRequired: '계획을 선택해주세요.',
      timeoutError: '요청 시간이 초과되었습니다. 다시 시도해주세요.',
      invalidDateRange: '시작일은 종료일보다 이전이어야 합니다.',
      invalidBudget: '예산은 0 이상이어야 합니다.',
      frequencyDayRequired: '빈도 일자를 입력해주세요.',
      processing: '처리중입니다...',
      loginCancelled: '로그인이 취소되었습니다.',
      authRequestNotFound: '인증 요청을 찾을 수 없습니다. 다시 로그인해주세요.',
      googleAccountExists: '이미 존재하는 구글 계정입니다. 기존 계정으로 로그인하세요.',
      appleTokenMissing: 'Apple 로그인에서 토큰을 받지 못했습니다.',
      authCodeMissing: '인증 코드를 받지 못했습니다.',
      fetchFailed: '불러오기에 실패했습니다.',
      completeSuccess: '계획이 완료되었습니다.',
      scheduleDeleteFailed: '일정 삭제에 실패했습니다.',
      planCompleteFailed: '계획 완료에 실패했습니다.',
      noDreamId: 'Dream ID가 없습니다.',
      noItemDataToEdit: '수정할 아이템 정보가 없습니다.',
      noItemId: '아이템 ID가 없습니다.',
      firstCreateMainCategory: '먼저 메인 카테고리를 생성해주세요.',
      subCategoryRequired: '메인 카테고리와 서브 카테고리 이름을 입력해주세요.',
      loginInThisWindow: '이 창에서 로그인합니다.',
      googleLoginUrlFetchFailed: '구글 로그인 URL을 가져오는데 실패했습니다.',
      googleLoginInProgressError: '구글 로그인 중 오류가 발생했습니다. 다시 시도해주세요.',
      popupBlocked: '팝업을 열 수 없습니다. 브라우저 설정에서 팝업 차단을 해제해주세요.',
      switchToWebLogin: '웹 로그인으로 진행합니다.',
      cannotStartGoogleLogin: '구글 로그인을 시작할 수 없습니다.',
      cannotStartAppleLogin: 'Apple 로그인을 시작할 수 없습니다.',
      goalRequired: '목표를 선택해주세요.',
      dreamRequired: '꿈을 선택해주세요.',
    },
    // Dashboard
    dashboard: {
      title: '통계',
      dreamList: '꿈 목록',
      dreamStatusPieChart: '꿈 진행 상태',
      progressBarChart: '꿈별 진행률',
      todayActionItems: '오늘의 할일',
      noDreams: '꿈이 없습니다.',
      noDreamsDesc: '첫 번째 꿈을 만들어보세요!',
      noData: '데이터가 없습니다.',
      noChartDataDesc: '데이터가 없습니다.',
      noActions: '할일이 없습니다.',
      noActionsDesc: '선택한 기간에 할일이 없습니다.',
      ready: '준비중',
      inProgress: '진행중',
      completed: '완료',
      progressRate: '진행률',
    },
    // Dreams
    dreams: {
      title: '꿈',
      createButton: '+ 꿈 생성',
      createTitle: '꿈 생성',
      editTitle: '꿈 수정',
      noDreams: '꿈이 없습니다. 꿈을 생성해보세요.',
      noDreamsDesc: '첫 번째 꿈을 만들어보세요!',
      dreamNameRequired: '꿈 이름을 입력해주세요.',
      dreamName: '꿈 이름',
      dreamNamePlaceholder: '꿈 이름을 입력하세요',
      dreamDesc: '꿈 설명',
      dreamDescPlaceholder: '꿈 설명을 입력하세요',
      startDate: '시작일',
      endDate: '종료일',
      budget: '예산 (원)',
      budgetPlaceholder: '예산을 입력하세요',
      accountCreatedTitle: '계좌 생성 완료',
      accountCreatedMessage: '꿈 생성과 함께 계좌가 자동으로 생성되었습니다.',
      accountCreatedInfo: '계좌 정보는 계좌 관리에서 확인할 수 있습니다.',
      accountName: '계좌명',
      deleteConfirm: '정말로 이 꿈을 삭제하시겠습니까?',
      edit: '수정',
      delete: '삭제',
      budgetLabel: '예산',
      goalBudget: '꿈 예산',
      itemBudget: '아이템 예산',
      itemTotalBudget: '아이템 예산',
      myDream: '내꿈',
      groupDream: '그룹꿈',
      confirmShare: '이 꿈을 공유하시겠습니까?',
      confirmUnshare: '공유를 해제하시겠습니까?',
      categories: '카테고리',
      creating: '꿈 생성 중입니다...',
      group: {
        addMember: '멤버 추가',
        emailPlaceholder: '이메일을 입력해주세요.',
        ownerTitle: '그룹 오너',
        membersTitle: '그룹 멤버',
        addButton: '추가',
        removeSuccess: '그룹 멤버가 제거되었습니다.',
        addSuccess: '그룹 멤버가 추가되었습니다.',
      },
      mainCategory: '메인 카테고리',
      subCategory: '서브 카테고리',
      selectMainCategory: '메인 카테고리를 선택하세요',
      selectedCategories: '선택된 카테고리',
      noCategories: '카테고리가 없습니다.',
      noMainCategories: '메인 카테고리가 없습니다.',
      noSubCategories: '서브 카테고리가 없습니다.',
      status: '상태',
      progress: '진행률',
      statusAutoCalculated: '상태는 목표와 계획의 완료율에 따라 자동으로 계산됩니다.',
      statusNote: '※ 상태는 목표와 계획의 완료율에 따라 자동으로 계산됩니다.',
      accounts: '계좌 관리',
    },
    // Goals
    goals: {
      title: '목표',
      createButton: '+ 목표 생성',
      goalName: '목표 이름',
      goalNamePlaceholder: '목표 이름을 입력하세요',
      noGoals: '목표가 없습니다. 목표를 생성해보세요.',
      noGoalsDesc: '첫 번째 목표를 만들어보세요!',
      goalDetail: '목표 상세',
      deleteGoalConfirm: '정말로 이 목표를 삭제하시겠습니까?',
      editGoalTitle: '목표 수정',
    },
    // Category
    category: {
      name: '카테고리 이름',
      editTitle: '카테고리 수정',
      deleteConfirm: '정말로 이 카테고리를 삭제하시겠습니까?',
      noPlans: '이 카테고리에 계획이 없습니다.',
    },
    // Goal Detail
    goalDetail: {
      budgetManagement: '예산 관리',
      planList: '계획 목록',
      createPlanButton: '+ 계획 생성',
      noAccount: '계좌가 없습니다.',
      currentBalance: '현재 잔액',
      targetAmount: '목표 금액',
      budget: '예산',
      noPlans: '계획이 없습니다. 계획을 생성해보세요.',
      noPlansDesc: '계획을 추가해보세요!',
      planName: '계획 이름',
      planDesc: '계획 설명',
      planNamePlaceholder: '계획 이름을 입력하세요',
      planNameRequired: '계획 이름을 입력해주세요.',
      startDate: '시작일',
      endDate: '종료일',
      status: '상태',
      progress: '진행률',
      deletePlanConfirm: '정말로 이 계획을 삭제하시겠습니까?',
      editPlanTitle: '계획 수정',
      createPlanTitle: '계획 생성',
      frequency: '반복 주기',
      frequencyDay: '반복 요일/일',
      setTransferSchedule: '반복 이체 스케줄 설정',
      transferScheduleTitle: '반복 이체 스케줄 설정',
      transferScheduleDesc: '계좌',
      transferAmount: '이체 금액',
      transferAmountPlaceholder: '이체할 금액을 입력하세요',
      transferScheduleCreated: '반복 이체 스케줄이 생성되었습니다.',
      transferScheduleFailed: '반복 이체 스케줄 생성에 실패했습니다.',
      transferScheduleError: '이체 금액을 입력해주세요.',
      completeConfirm: '체크리스트가 모두 완료되지 않았을 수 있습니다. 완료로 표시하시겠습니까?',
    },
    // Plan
    plan: {
      frequency: {
        none: '없음',
        daily: '매일',
        weekly: '매주',
        monthly: '매월',
        yearly: '매년',
      },
      frequencyDay: '반복 요일/일',
    },
    // Checklist
    checklist: {
      title: '체크리스트',
      addItem: '+ 아이템 준비',
      noItems: '준비할 아이템이 없습니다.',
      noItemsDesc: '준비할 아이템을 추가해보세요!',
      addItemTitle: '아이템 준비',
      editItemTitle: '아이템 수정',
      itemName: '아이템 이름',
      itemNamePlaceholder: '아이템 이름을 입력하세요',
      itemNameRequired: '아이템 이름을 입력해주세요.',
      itemDesc: '아이템 설명',
      itemDescPlaceholder: '아이템 설명을 입력하세요',
      itemLink: '링크 (선택사항)',
      itemLinkPlaceholder: 'https://example.com',
      openLink: '링크 열기 →',
      deleteItemConfirm: '정말로 이 항목을 삭제하시겠습니까?',
    },
    // Calendar
    calendar: {
      title: '캘린더',
      actionsList: '계획 목록',
      noPlans: '계획이 없습니다.',
      noPlansDesc: '이 날짜에 계획이 없습니다.',
      createAction: '계획 생성',
      editAction: '계획 수정',
      dreamSelect: '꿈 선택',
      selectDream: '꿈을 선택하세요',
      goalSelect: '목표 선택',
      selectGoal: '목표를 선택하세요',
      planSelect: '계획 선택',
      planIdPlaceholder: '계획 ID를 입력하세요',
      selectPlan: '계획을 선택하세요',
      actionName: '계획 이름',
      actionNamePlaceholder: '계획 이름을 입력하세요',
      actionNameRequired: '계획 이름을 입력해주세요.',
      dreamName: '꿈 이름',
      goalName: '목표 이름',
      planName: '계획 이름',
      date: '날짜',
      time: '시간',
      description: '설명',
      descriptionPlaceholder: '계획 설명을 입력하세요',
      deleteConfirm: '정말로 이 계획을 삭제하시겠습니까?',
      completeConfirm: '이 계획을 완료하시겠습니까? (오늘까지만 표시됩니다)',
      pending: '대기',
      completed: '완료',
      goal: '목표',
      selectType: '생성할 항목 선택',
      createPlan: '계획 생성',
      selectDay: '선택하세요',
      monday: '월요일',
      tuesday: '화요일',
      wednesday: '수요일',
      thursday: '목요일',
      friday: '금요일',
      saturday: '토요일',
      sunday: '일요일',
      deleteOptions: '일정 삭제 옵션',
      deleteOptionsMessage: '삭제할 일정을 선택하세요',
      deleteThisInstance: '이 일정만 삭제',
      deleteFollowing: '이후 일정 삭제',
      deleteAll: '전체 일정 삭제',
    },
    // Coaching
    coaching: {
      title: '코칭',
      dreamSelect: '꿈 선택',
      noDreams: '꿈이 없습니다.',
      coachMessages: '코치 메시지',
      generateCoach: '코치 생성',
      noMessages: '코치 메시지가 없습니다.',
      noMessagesDesc: '코칭 메시지가 없습니다.',
      noDreamsDesc: '꿈을 만들어보세요!',
      completionRate: '완료율',
      budgetWarning: '⚠️ 예산 경고',
      scheduleDelay: '⚠️ 일정 지연',
      userId: '사용자 ID',
    },
    // Share
    share: {
      title: '공유',
      noFeeds: '공유된 피드가 없습니다.',
      like: '좋아요',
      copy: '가져오기',
    },
    // User Management
    userManagement: {
      title: '유저 관리',
      login: '로그인',
      localLogin: '일반 로그인',
      signup: '회원가입',
      profile: '프로필',
      email: '이메일',
      password: '비밀번호',
      username: '사용자 이름',
      role: '역할',
      account: '계정',
      loginButton: '로그인',
      signupButton: '회원가입',
      logoutButton: '로그아웃',
      updateButton: '수정',
      deleteButton: '삭제',
      emailPlaceholder: '이메일을 입력하세요',
      passwordPlaceholder: '비밀번호를 입력하세요',
      usernamePlaceholder: '사용자 이름을 입력하세요',
      emailNotEditable: '이메일은 수정할 수 없습니다.',
      loginWithGoogle: '구글로 로그인',
      guestLogin: '게스트로 로그인',
      loginDescription: '꿈을 이루기 위한 여정을 시작하세요.',
      guestUser: '게스트 유저',
      guestUserDescription: '게스트로 로그인하셨습니다. 일반 계정으로 전환하여 더 많은 기능을 이용하세요.',
      switchToRegularAccount: '일반 계정으로 전환',
      linkAccount: '다른 계정으로 연동',
      linkAccountDescription: '구글 또는 Apple 계정으로 연동하면 현재 데이터가 해당 계정에 연결됩니다.',
      linkWithGoogle: 'Google로 연동',
      linkWithApple: 'Apple로 연동',
      switchToLoginConfirmTitle: '로그인 전환',
      switchToLoginConfirmMessage: '로그인 전환하시겠습니까? 소셜 계정으로 전환하면 현재 데이터가 해당 계정에 연결됩니다.',
      guestUserBadge: '게스트',
      logoutDesc: '로그아웃하면 다시 로그인해야 합니다.',
      logoutConfirm: '로그아웃 하시겠습니까?',
      loginWithApple: 'Apple로 로그인',
      signInWithLegacyAccount: '기존 계정으로 로그인',
      legacyLoginTitle: '기존 계정으로 로그인',
      loginFailed: '로그인 실패',
      proExpired: '만료됨',
      proNone: 'PRO 없음',
      proRemainingDay: '일 남음',
      proRemainingDays: '일 남음',
      proRemainingHour: '시간 남음',
      proRemainingHours: '시간 남음',
      proRemainingMinute: '분 남음',
      proRemainingMinutes: '분 남음',
      editName: '이름 수정',
      changeLanguage: '언어 변경',
    },
    // My Page
    myPage: {
      upgradeToPro: 'PRO로 업그레이드',
      upgradeDescription: '모든 기능을 무제한으로 이용해보세요.',
      settings: '설정',
    },
    // Admin
    admin: {
      unauthorized: '접근 권한이 없습니다.',
      unauthorizedDesc: '관리자만 접근할 수 있는 페이지입니다.',
      totalUsers: '전체 사용자',
      totalDreams: '전체 꿈',
      proUsers: 'PRO 사용자',
      normalUsers: '일반 사용자',
      guestUsers: '게스트 사용자',
      coachGeneration: '코치 생성',
      coachGenerationDesc: '모든 사용자의 꿈에 대해 코치를 생성합니다. 스케줄러가 자동으로 실행하지만 수동으로도 생성할 수 있습니다.',
      userList: '사용자 목록',
      noUsers: '사용자가 없습니다.',
      username: '사용자명',
      email: '이메일',
      role: '역할',
      proExpireDate: 'PRO 만료일',
      loadUsersFailed: '사용자 목록을 불러오는데 실패했습니다.',
    },
    // Accounts
    accounts: {
      title: '계좌 관리',
      createButton: '+ 계좌 생성',
      createTitle: '계좌 생성',
      editTitle: '계좌 수정',
      noAccounts: '계좌가 없습니다.',
      noAccountsDesc: '첫 번째 계좌를 생성해보세요!',
      accountName: '계좌 이름',
      accountNamePlaceholder: '계좌 이름을 입력하세요',
      accountNameRequired: '계좌 이름을 입력해주세요.',
      dreamSelect: '꿈 선택',
      selectDream: '꿈을 선택하세요',
      dreamRequired: '꿈을 선택해주세요.',
      currentBalance: '현재 잔액',
      targetAmount: '목표 금액',
      invalidBalance: '잔액은 0 이상이어야 합니다.',
      invalidTargetAmount: '목표 금액은 0보다 커야 합니다.',
      invalidAmount: '금액은 0보다 커야 합니다.',
      insufficientBalance: '잔액이 부족합니다.',
      deposit: '입금',
      withdraw: '출금',
      depositTitle: '입금',
      withdrawTitle: '출금',
      depositSuccess: '입금이 완료되었습니다.',
      depositFailed: '입금에 실패했습니다.',
      withdrawSuccess: '출금이 완료되었습니다.',
      withdrawFailed: '출금에 실패했습니다.',
      transactions: '거래 내역',
      transactionsTitle: '거래 내역',
      noTransactions: '거래 내역이 없습니다.',
      noTransactionsDesc: '아직 거래 내역이 없습니다.',
      transactionFetchFailed: '거래 내역 조회에 실패했습니다.',
      amount: '금액',
      amountPlaceholder: '금액을 입력하세요',
      deleteConfirm: '정말로 이 계좌를 삭제하시겠습니까?',
      adjustBalance: '잔액 조정',
      adjustBalanceTitle: '잔액 조정',
      newBalance: '새 잔액',
      newBalancePlaceholder: '새 잔액을 입력하세요',
      balanceAdjustSuccess: '잔액이 조정되었습니다.',
      balanceAdjustFailed: '잔액 조정에 실패했습니다.',
    },
    // Settings
    settings: {
      general: '일반',
      language: '언어',
      account: '계정',
      accountManagement: '계정 관리',
      accountManagementDesc: '프로필 수정, 비밀번호 변경',
      deleteAccount: '계정 삭제',
      deleteAccountDesc: '계정과 모든 데이터가 영구적으로 삭제됩니다.',
      deleteAccountConfirm: '정말로 계정을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.',
      legal: '법적 정보',
      terms: '이용약관',
      privacy: '개인정보처리방침',
      appInfo: '앱 정보',
      version: '버전',
      pointsAndSubscription: '포인트 및 구독',
      pointBalance: '포인트 잔액',
      userRole: '사용자 등급',
      chargePoints: '포인트 충전',
      chargePointsDesc: '인앱 결제를 통해 포인트를 충전합니다.',
      charge: '충전',
      purchasePro: 'PRO 연장',
      purchaseProDesc: '보상형 광고를 끝까지 시청하면 PRO가 연장됩니다. (내 정보 → PRO 연장)',
      purchase: '구매',
      paymentToken: '결제 토큰',
      paymentTokenPlaceholder: '인앱 결제 토큰을 입력하세요',
      amount: '충전할 포인트',
      durationMonths: '구독 기간 (개월)',
      purchaseProInfo: 'PRO 구독을 구매하면 Dream과 Plan 생성 제한이 해제됩니다.',
      monthlyPrice: '월 구독료',
      totalCost: '총 비용',
      currentBalance: '현재 잔액',
      insufficientPoints: '포인트가 부족합니다.',
      planUsageLimit: '플랜별 이용 한도',
      limitUnlimited: '무제한',
      limitDream: '꿈',
      limitGoal: '목표',
      limitPlan: '계획',
      limitItem: '아이템',
    },
    // Error
    error: {
      notFound: '페이지를 찾을 수 없습니다',
      notFoundDesc: '요청하신 페이지가 존재하지 않거나 이동되었습니다.',
      serverError: '서버 오류',
      serverErrorDesc: '서버에서 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
      networkError: '네트워크 오류',
      networkErrorDesc: '인터넷 연결을 확인하고 다시 시도해주세요.',
      generic: '오류가 발생했습니다',
      genericDesc: '예상치 못한 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
      error: '오류',
      goHome: '홈으로 이동',
    },
    // Terms
    terms: {
      title: 'Dreamity 이용약관',
      lastUpdated: '최종 수정일: 2026년 2월 1일',
    },
    // Privacy
    privacy: {
      title: 'Dreamity 개인정보처리방침',
      lastUpdated: '최종 수정일: 2026년 2월 1일',
    },
  },
  en: {
    // Common
    common: {
      loading: 'Loading...',
      error: 'Error',
      cancel: 'Cancel',
      confirm: 'Confirm',
      later: 'Later',
      upgrade: 'Upgrade',
      save: 'Save',
      change: 'Change',
      go: 'Go',
      view: 'View',
      delete: 'Delete',
      edit: 'Edit',
      create: 'Create',
      close: 'Close',
      back: 'Back',
      share: 'Share',
      shared: 'Shared',
      completed: 'Completed',
      inProgress: 'In Progress',
      preparing: 'Preparing',
      yes: 'Yes',
      no: 'No',
      search: 'Search',
      filter: 'Filter',
      all: 'All',
      none: 'None',
      today: 'Today',
      yesterday: 'Yesterday',
      tomorrow: 'Tomorrow',
      info: 'Info',
      refresh: 'Refresh',
      backToLogin: 'Back to Login',
      or: 'or',
      group: 'Group',
      account: 'Account',
    },
    // Currency
    currency: {
      unit: '$',
    },
    // Page titles
    pages: {
      dashboard: 'Dashboard',
      dreams: 'Dreams',
      goals: 'Goals',
      plans: 'Plans',
      checklist: 'Checklist',
      calendar: 'Calendar',
      coaching: 'Coaching',
      share: 'Feed',
      userManagement: 'User Management',
      settings: 'Settings',
      guide: 'User Guide',
    },
    // Messages
    messages: {
      noData: 'No data available.',
      createSuccess: 'Created successfully.',
      updateSuccess: 'Updated successfully.',
      deleteSuccess: 'Deleted successfully.',
      deleteConfirm: 'Are you sure you want to delete?',
      deleteConfirmTitle: 'Confirm Delete',
      accountCreated: 'Account has been automatically created.',
      dreamCopied: 'Shared dream has been copied to your dreams.',
      createFailed: 'Failed to create.',
      updateFailed: 'Failed to update.',
      deleteFailed: 'Failed to delete.',
      shareFailed: 'Failed to set share status.',
      copyFailed: 'Failed to copy.',
      likeFailed: 'Failed to toggle like.',
      coachDeleteFailed: 'Failed to delete coach.',
      coachGenerateSuccess: 'Coach generated successfully.',
      coachGenerateFailed: 'Failed to generate coach.',
      generating: 'Generating...',
      actionCreateFailed: 'Failed to create todo.',
      actionUpdateFailed: 'Failed to update todo.',
      actionDeleteFailed: 'Failed to delete todo.',
      shareSuccess: 'Shared successfully.',
      unshareSuccess: 'Unshared successfully.',
              itemCreateFailed: 'Failed to create item.',
              itemUpdateFailed: 'Failed to update item.',
              itemDeleteFailed: 'Failed to delete item.',
              statusChangeFailed: 'Failed to change status.',
      planSelectRequired: 'Please select a plan.',
      timeoutError: 'Request timeout. Please try again.',
      invalidDateRange: 'Start date must be before end date.',
      invalidBudget: 'Budget must be 0 or greater.',
      frequencyDayRequired: 'Please enter frequency day.',
      processing: 'Processing...',
      loginCancelled: 'Login was cancelled.',
      authRequestNotFound: 'Authentication request not found. Please try logging in again.',
      googleAccountExists: 'This Google account already exists. Please sign in with your existing account.',
      appleTokenMissing: 'No token was received from Apple login.',
      authCodeMissing: 'Failed to receive authentication code.',
      fetchFailed: 'Failed to fetch data.',
      completeSuccess: 'Plan completed successfully.',
      scheduleDeleteFailed: 'Failed to delete schedule.',
      planCompleteFailed: 'Failed to complete plan.',
      noDreamId: 'Dream ID is missing.',
      noItemDataToEdit: 'No item data available to edit.',
      noItemId: 'Item ID is missing.',
      firstCreateMainCategory: 'Please create a main category first.',
      subCategoryRequired: 'Please select a main category and enter a sub category name.',
      loginInThisWindow: 'Continue login in this window.',
      googleLoginUrlFetchFailed: 'Failed to fetch Google login URL.',
      googleLoginInProgressError: 'An error occurred during Google login. Please try again.',
      popupBlocked: 'Unable to open popup. Please disable popup blocking in your browser settings.',
      switchToWebLogin: 'Proceeding with web login.',
      cannotStartGoogleLogin: 'Unable to start Google login.',
      cannotStartAppleLogin: 'Unable to start Apple login.',
      goalRequired: 'Please select a goal.',
      dreamRequired: 'Please select a dream.',
    },
    // Dashboard
    dashboard: {
      title: 'Statistics',
      dreamList: 'Dream List',
      dreamStatusPieChart: 'Dream Progress Status',
      progressBarChart: 'Dream Progress Rate',
      todayActionItems: "Today's Action Items",
      noDreams: 'No dreams available.',
      noData: 'No data available.',
      noActions: 'No todos available.',
      ready: 'Preparing',
      inProgress: 'In Progress',
      completed: 'Completed',
      progressRate: 'Progress Rate',
    },
    // Dreams
    dreams: {
      title: 'Dreams',
      createButton: '+ Create Dream',
      createTitle: 'Create Dream',
      editTitle: 'Edit Dream',
      noDreams: 'No dreams available. Create a dream.',
      noDreamsDesc: 'Create your first dream!',
      dreamName: 'Dream Name',
      dreamNamePlaceholder: 'Enter dream name',
      dreamNameRequired: 'Please enter a dream name.',
      dreamDesc: 'Dream Description',
      dreamDescPlaceholder: 'Enter dream description',
      startDate: 'Start Date',
      endDate: 'End Date',
      budget: 'Budget (KRW)',
      budgetPlaceholder: 'Enter budget',
      accountCreatedTitle: 'Account Created',
      accountCreatedMessage: 'Account has been automatically created with the dream.',
      accountCreatedInfo: 'You can check account information in account management.',
      accountName: 'Account Name',
      deleteConfirm: 'Are you sure you want to delete this dream?',
      edit: 'Edit',
      delete: 'Delete',
      budgetLabel: 'Budget',
      goalBudget: 'Dream Budget',
      itemBudget: 'Item Budget',
      itemTotalBudget: 'Item Budget',
      myDream: 'My Dream',
      groupDream: 'Group Dream',
      confirmShare: 'Do you want to share this dream?',
      confirmUnshare: 'Do you want to unshare this dream?',
      categories: 'Categories',
      creating: 'Creating dream...',
      group: {
        addMember: 'Add Member',
        emailPlaceholder: 'Enter email address',
        ownerTitle: 'Group Owner',
        membersTitle: 'Group Members',
        addButton: 'Add',
        removeSuccess: 'Group member removed.',
        addSuccess: 'Group member added.',
      },
      mainCategory: 'Main Category',
      subCategory: 'Sub Category',
      selectMainCategory: 'Select main category',
      selectedCategories: 'Selected Categories',
      noCategories: 'No categories available.',
      noMainCategories: 'No main categories available.',
      noSubCategories: 'No sub categories available.',
      status: 'Status',
      progress: 'Progress',
      statusAutoCalculated: 'Status is automatically calculated based on goal and plan completion rates.',
      statusNote: '※ Status is automatically calculated based on goal and plan completion rates.',
    },
    // Goals
    goals: {
      title: 'Goals',
      createButton: '+ Create Goal',
      goalName: 'Goal Name',
      goalNamePlaceholder: 'Enter goal name',
      noGoals: 'No goals available. Create a goal.',
      noGoalsDesc: 'Create your first goal!',
      goalDetail: 'Goal Detail',
      deleteGoalConfirm: 'Are you sure you want to delete this goal?',
      editGoalTitle: 'Edit Goal',
    },
    // My Page
    myPage: {
      upgradeToPro: 'Upgrade to PRO',
      upgradeDescription: 'Enjoy all features without limits.',
      settings: 'Settings',
    },
    // Category
    category: {
      name: 'Category Name',
      editTitle: 'Edit Category',
      deleteConfirm: 'Are you sure you want to delete this category?',
      noPlans: 'No plans in this category.',
    },
    // Goal Detail
    goalDetail: {
      budgetManagement: 'Budget Management',
      planList: 'Plan List',
      createPlanButton: '+ Create Plan',
      noAccount: 'No account available.',
      currentBalance: 'Current Balance',
      targetAmount: 'Target Amount',
      budget: 'Budget',
      noPlans: 'No plans available. Create a plan.',
      noPlansDesc: 'Add a plan!',
      planName: 'Plan Name',
      planDesc: 'Plan Description',
      planNamePlaceholder: 'Enter plan name',
      planNameRequired: 'Please enter a plan name.',
      startDate: 'Start Date',
      endDate: 'End Date',
      status: 'Status',
      progress: 'Progress',
      deletePlanConfirm: 'Are you sure you want to delete this plan?',
      editPlanTitle: 'Edit Plan',
      createPlanTitle: 'Create Plan',
      frequency: 'Frequency',
      frequencyDay: 'Frequency Day',
      setTransferSchedule: 'Set Recurring Transfer Schedule',
      transferScheduleTitle: 'Set Recurring Transfer Schedule',
      transferScheduleDesc: 'Account',
      transferAmount: 'Transfer Amount',
      transferAmountPlaceholder: 'Enter transfer amount',
      transferScheduleCreated: 'Recurring transfer schedule has been created.',
      transferScheduleFailed: 'Failed to create recurring transfer schedule.',
      transferScheduleError: 'Please enter transfer amount.',
      completeConfirm: 'Some checklist items may not be completed. Mark as completed anyway?',
    },
    // Plan
    plan: {
      frequency: {
        none: 'None',
        daily: 'Daily',
        weekly: 'Weekly',
        monthly: 'Monthly',
        yearly: 'Yearly',
      },
      frequencyDay: 'Frequency Day',
    },
    // Checklist
    checklist: {
      title: 'Checklist',
      addItem: '+ Prepare Item',
      noItems: 'No items to prepare.',
      noItemsDesc: 'Add items to prepare!',
      addItemTitle: 'Prepare Item',
      editItemTitle: 'Edit Item',
      itemName: 'Item Name',
      itemNamePlaceholder: 'Enter item name',
      itemNameRequired: 'Please enter an item name.',
      itemDesc: 'Item Description',
      itemDescPlaceholder: 'Enter item description',
      itemLink: 'Link (Optional)',
      itemLinkPlaceholder: 'https://example.com',
      openLink: 'Open Link →',
      deleteItemConfirm: 'Are you sure you want to delete this item?',
    },
    // Calendar
    calendar: {
      title: 'Calendar',
      actionsList: 'Todo List',
      noActions: 'No todos available.',
      noPlans: 'No plans available.',
      noPlansDesc: 'No plans on this date.',
      createAction: 'Create Todo',
      editAction: 'Edit Todo',
      dreamSelect: 'Select Dream',
      selectDream: 'Select a dream',
      goalSelect: 'Select Goal',
      selectGoal: 'Select a goal',
      planSelect: 'Select Plan',
      planIdPlaceholder: 'Enter plan ID',
      selectPlan: 'Select a plan',
      actionName: 'Todo Name',
      actionNamePlaceholder: 'Enter todo name',
      actionNameRequired: 'Please enter a todo name.',
      dreamName: 'Dream Name',
      goalName: 'Goal Name',
      planName: 'Plan Name',
      date: 'Date',
      time: 'Time',
      description: 'Description',
      descriptionPlaceholder: 'Enter todo description',
      deleteConfirm: 'Are you sure you want to delete this todo?',
      completeConfirm: 'Complete this plan? (Will only show until today)',
      pending: 'Pending',
      completed: 'Completed',
      goal: 'Goal',
      selectType: 'Select Item to Create',
      createPlan: 'Create Plan',
      selectDay: 'Please select',
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday',
      deleteOptions: 'Delete Options',
      deleteOptionsMessage: 'Choose how to delete this event',
      deleteThisInstance: 'Delete This Instance',
      deleteFollowing: 'Delete Following Events',
      deleteAll: 'Delete All Events',
    },
    // Coaching
    coaching: {
      title: 'Coaching',
      dreamSelect: 'Select Dream',
      noDreams: 'No dreams available.',
      coachMessages: 'Coach Messages',
      generateCoach: 'Generate Coach',
      noMessages: 'No coach messages available.',
      noMessagesDesc: 'No coaching messages available.',
      noDreamsDesc: 'Create a dream!',
      completionRate: 'Completion Rate',
      budgetWarning: '⚠️ Budget Warning',
      scheduleDelay: '⚠️ Schedule Delay',
      userId: 'User ID',
    },
    // Share
    share: {
      title: 'Share',
      noFeeds: 'No shared feeds available.',
      like: 'Like',
      copy: 'Copy',
    },
    // User Management
    userManagement: {
      title: 'User Management',
      login: 'Login',
      localLogin: 'Local Login',
      signup: 'Sign Up',
      profile: 'Profile',
      email: 'Email',
      password: 'Password',
      username: 'Username',
      role: 'Role',
      account: 'Account',
      loginButton: 'Login',
      signupButton: 'Sign Up',
      logoutButton: 'Logout',
      updateButton: 'Update',
      deleteButton: 'Delete',
      emailPlaceholder: 'Enter email',
      passwordPlaceholder: 'Enter password',
      usernamePlaceholder: 'Enter username',
      emailNotEditable: 'Email cannot be modified.',
      loginWithGoogle: 'Sign in with Google',
      guestLogin: 'Login as Guest',
      loginDescription: 'Start your journey to achieve your dreams.',
      guestUser: 'Guest User',
      guestUserDescription: 'You are logged in as a guest. Switch to a regular account to access more features.',
      switchToRegularAccount: 'Switch to Regular Account',
      linkAccount: 'Link another account',
      linkAccountDescription: 'Linking with Google or Apple will connect your current data to that account.',
      linkWithGoogle: 'Link with Google',
      linkWithApple: 'Link with Apple',
      switchToLoginConfirmTitle: 'Switch login',
      switchToLoginConfirmMessage: 'Switch to this account? Your current data will be linked to the selected account.',
      guestUserBadge: 'Guest',
      logoutDesc: 'You will need to log in again after logging out.',
      logoutConfirm: 'Do you want to log out?',
      loginWithApple: 'Sign in with Apple',
      signInWithLegacyAccount: 'Sign in with Legacy Account',
      legacyLoginTitle: 'Sign in with Legacy Account',
      loginFailed: 'Login failed',
      proExpired: 'Expired',
      proNone: 'No PRO',
      proRemainingDay: 'day left',
      proRemainingDays: 'days left',
      proRemainingHour: 'hour left',
      proRemainingHours: 'hours left',
      proRemainingMinute: 'minute left',
      proRemainingMinutes: 'minutes left',
      editName: 'Edit Name',
      changeLanguage: 'Change Language',
    },
    // Admin
    admin: {
      unauthorized: 'Unauthorized Access',
      unauthorizedDesc: 'This page is only accessible to administrators.',
      totalUsers: 'Total Users',
      totalDreams: 'Total Dreams',
      proUsers: 'PRO Users',
      normalUsers: 'Normal Users',
      guestUsers: 'Guest Users',
      coachGeneration: 'Coach Generation',
      coachGenerationDesc: 'Generate coaches for all users\' dreams. The scheduler runs automatically, but you can also generate manually.',
      userList: 'User List',
      noUsers: 'No users found.',
      username: 'Username',
      email: 'Email',
      role: 'Role',
      proExpireDate: 'PRO Expiry Date',
      loadUsersFailed: 'Failed to load user list.',
    },
    // Accounts
    accounts: {
      title: 'Account Management',
      createButton: '+ Create Account',
      createTitle: 'Create Account',
      editTitle: 'Edit Account',
      noAccounts: 'No accounts available.',
      noAccountsDesc: 'Create your first account!',
      accountName: 'Account Name',
      accountNamePlaceholder: 'Enter account name',
      accountNameRequired: 'Please enter an account name.',
      dreamSelect: 'Select Dream',
      selectDream: 'Select a dream',
      dreamRequired: 'Please select a dream.',
      currentBalance: 'Current Balance',
      targetAmount: 'Target Amount',
      invalidBalance: 'Balance must be 0 or greater.',
      invalidTargetAmount: 'Target amount must be greater than 0.',
      invalidAmount: 'Amount must be greater than 0.',
      insufficientBalance: 'Insufficient balance.',
      deposit: 'Deposit',
      withdraw: 'Withdraw',
      depositTitle: 'Deposit',
      withdrawTitle: 'Withdraw',
      depositSuccess: 'Deposit completed successfully.',
      depositFailed: 'Deposit failed.',
      withdrawSuccess: 'Withdrawal completed successfully.',
      withdrawFailed: 'Withdrawal failed.',
      transactions: 'Transactions',
      transactionsTitle: 'Transaction History',
      noTransactions: 'No transactions available.',
      noTransactionsDesc: 'No transaction history yet.',
      transactionFetchFailed: 'Failed to fetch transaction history.',
      amount: 'Amount',
      amountPlaceholder: 'Enter amount',
      deleteConfirm: 'Are you sure you want to delete this account?',
      adjustBalance: 'Adjust Balance',
      adjustBalanceTitle: 'Adjust Balance',
      newBalance: 'New Balance',
      newBalancePlaceholder: 'Enter new balance',
      balanceAdjustSuccess: 'Balance adjusted successfully.',
      balanceAdjustFailed: 'Failed to adjust balance.',
    },
    // Settings
    settings: {
      general: 'General',
      language: 'Language',
      account: 'Account',
      accountManagement: 'Account Management',
      accountManagementDesc: 'Edit profile, change password',
      deleteAccount: 'Delete Account',
      deleteAccountDesc: 'Your account and all data will be permanently deleted.',
      deleteAccountConfirm: 'Are you sure you want to delete your account? This action cannot be undone.',
      legal: 'Legal',
      terms: 'Terms of Service',
      privacy: 'Privacy Policy',
      appInfo: 'App Information',
      version: 'Version',
      pointsAndSubscription: 'Points & Subscription',
      pointBalance: 'Point Balance',
      userRole: 'User Role',
      chargePoints: 'Charge Points',
      chargePointsDesc: 'Charge points through in-app purchase.',
      charge: 'Charge',
      purchasePro: 'Extend PRO',
      purchaseProDesc: 'Watch a rewarded ad to extend PRO. (Account → Extend PRO)',
      purchase: 'Purchase',
      paymentToken: 'Payment Token',
      paymentTokenPlaceholder: 'Enter in-app purchase token',
      amount: 'Amount to Charge',
      durationMonths: 'Duration (Months)',
      purchaseProInfo: 'Purchasing PRO subscription will remove Dream and Plan creation limits.',
      monthlyPrice: 'Monthly Price',
      totalCost: 'Total Cost',
      currentBalance: 'Current Balance',
      insufficientPoints: 'Insufficient points.',
      planUsageLimit: 'Plan usage limits',
      limitUnlimited: 'Unlimited',
      limitDream: 'Dream',
      limitGoal: 'Goal',
      limitPlan: 'Plan',
      limitItem: 'Item',
    },
    // Error
    error: {
      notFound: 'Page Not Found',
      notFoundDesc: 'The page you requested does not exist or has been moved.',
      serverError: 'Server Error',
      serverErrorDesc: 'An error occurred on the server. Please try again later.',
      networkError: 'Network Error',
      networkErrorDesc: 'Please check your internet connection and try again.',
      generic: 'An Error Occurred',
      genericDesc: 'An unexpected error occurred. Please try again later.',
      error: 'Error',
      goHome: 'Go Home',
    },
    // Terms
    terms: {
      title: 'Dreamity Terms of Service',
      lastUpdated: 'Last Updated: February 1, 2026',
    },
    // Privacy
    privacy: {
      title: 'Dreamity Privacy Policy',
      lastUpdated: 'Last Updated: February 1, 2026',
    },
  },
}

export function getTranslation(language: Language) {
  return translations[language]
}

export function t(key: string, language: Language = 'ko'): string {
  const keys = key.split('.')
  let value: any = translations[language]

  for (const k of keys) {
    value = value?.[k]
    if (value === undefined) {
      console.warn(`Translation key not found: ${key}`)
      return key
    }
  }

  return value
}

/** API에서 오는 진행 상태 한글('준비'|'진행중'|'완료')을 현재 언어로 번역 */
export function translateProgressStatus(status: string | undefined | null, language: Language): string {
  if (status == null || typeof status !== 'string') return ''
  const s = status.trim()
  const keyMap: Record<string, string> = {
    '준비': 'dashboard.ready',
    '준비중': 'dashboard.ready',
    '진행중': 'dashboard.inProgress',
    '완료': 'dashboard.completed',
  }
  const key = keyMap[s]
  return key ? t(key, language) : s
}
